/**
 * PaymentsPage
 * Client payment tracking: KPI summary, status/method filters, payment list,
 * and explicit offline-recording / online-collection workflows.
 */

import { useEffect, useState } from 'react'
import {
  Search,
  Plus,
  X,
  CreditCard,
  CheckCircle2,
  XCircle,
  Clock,
  Wallet,
} from 'lucide-react'
import { usePaymentStore } from '@/store/paymentStore'
import { toast } from '@/store/toastStore'
import PaymentTable from '@/components/payments/PaymentTable'
import PaymentModal from '@/components/payments/PaymentModal'
import type { Payment, PaymentStatus, PaymentMethod } from '@/types/payment.types'
import {
  PAYMENT_STATUS_OPTIONS,
  PAYMENT_STATUS_LABELS,
  PAYMENT_METHOD_OPTIONS,
  PAYMENT_METHOD_LABELS,
} from '@/types/payment.types'
import { useAuth } from '@/contexts/AuthContext'

interface KpiCardProps {
  label: string
  value: string
  icon: React.ReactNode
  iconWrapClass: string
}

function KpiCard({ label, value, icon, iconWrapClass }: KpiCardProps) {
  return (
    <div className="card card-elevated p-6 space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-text-secondary text-sm font-medium">{label}</div>
        <div className={`p-2 rounded-lg ${iconWrapClass}`}>{icon}</div>
      </div>
      <div className="text-3xl font-bold text-text-primary">{value}</div>
    </div>
  )
}

function formatCurrency(amount: number, currency: string): string {
  try {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency,
      maximumFractionDigits: 0,
    }).format(amount)
  } catch {
    return `${currency} ${amount.toLocaleString('en-IN')}`
  }
}

export default function PaymentsPage() {
  const { hasPermission } = useAuth()
  const canCreate = hasPermission('txh.payment.create')
  const canReconcile = hasPermission('txh.payment.reconcile')
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [searchInput, setSearchInput] = useState('')

  const {
    isLoading,
    stats,
    loadPayments,
    loadStats,
    filters,
    setFilters,
    setSearchQuery,
    getPaginatedPayments,
    processPayment,
    failPayment,
    reconcilePayment,
  } = usePaymentStore()

  // Load on mount (mock or real, per API mode)
  useEffect(() => {
    loadPayments()
    loadStats()
  }, [loadPayments, loadStats])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => setSearchQuery(searchInput), 300)
    return () => clearTimeout(timer)
  }, [searchInput, setSearchQuery])

  const { items: payments, total } = getPaginatedPayments()

  const handleCreateSuccess = () => {
    setShowCreateModal(false)
    loadStats()
  }

  const handleProcess = async (payment: Payment) => {
    if (!canReconcile || payment.gateway !== 'manual') return
    const transactionId = window.prompt(
      'Enter the gateway transaction ID to mark this payment as completed:',
      payment.transactionId ?? ''
    )
    if (transactionId === null) return
    if (!transactionId.trim()) {
      toast.error('Transaction ID is required', 'Provide the gateway transaction ID.')
      return
    }
    try {
      await processPayment(payment.id, transactionId.trim())
      toast.success('Payment processed', 'The payment is now completed.')
      loadStats()
    } catch (err) {
      toast.error(
        'Couldn’t process payment',
        err instanceof Error ? err.message : 'Please try again in a moment.'
      )
    }
  }

  const handleFail = async (payment: Payment) => {
    if (!canReconcile || payment.gateway !== 'manual') return
    const reason = window.prompt('Enter the reason for marking this payment as failed:')
    if (reason === null) return
    if (!reason.trim()) {
      toast.error('Reason is required', 'Provide a short failure reason.')
      return
    }
    try {
      await failPayment(payment.id, reason.trim())
      toast.success('Payment marked as failed')
      loadStats()
    } catch (err) {
      toast.error(
        'Couldn’t update payment',
        err instanceof Error ? err.message : 'Please try again in a moment.'
      )
    }
  }

  const handleReconcile = async (payment: Payment) => {
    if (!canReconcile || payment.gateway !== 'razorpay') return
    try {
      const updated = await reconcilePayment(payment.id)
      toast.success(
        'Provider status refreshed',
        `Razorpay reports this payment as ${updated.providerStatus ?? updated.status}.`
      )
      loadStats()
    } catch (err) {
      toast.error(
        'Could not refresh provider status',
        err instanceof Error ? err.message : 'Please try again in a moment.'
      )
    }
  }

  const currency = stats?.currency ?? 'INR'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-text-primary">Payments</h1>
          <p className="text-sm text-text-secondary mt-1">
            Record offline receipts or collect securely through Razorpay
          </p>
        </div>
        {canCreate && (
          <button
            onClick={() => setShowCreateModal(true)}
            className="btn btn-primary btn-sm flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            Add Payment
          </button>
        )}
      </div>

      {/* KPI Summary */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard
          label="Total Collected"
          value={formatCurrency(stats?.totalAmount ?? 0, currency)}
          icon={<Wallet className="w-6 h-6 text-primary-600" />}
          iconWrapClass="bg-primary-100 dark:bg-primary-900/30"
        />
        <KpiCard
          label="Successful"
          value={String(stats?.successfulPayments ?? 0)}
          icon={<CheckCircle2 className="w-6 h-6 text-green-600 dark:text-green-400" />}
          iconWrapClass="bg-green-100 dark:bg-green-900/30"
        />
        <KpiCard
          label="Pending"
          value={String(stats?.pendingPayments ?? 0)}
          icon={<Clock className="w-6 h-6 text-orange-600 dark:text-orange-400" />}
          iconWrapClass="bg-orange-100 dark:bg-orange-900/30"
        />
        <KpiCard
          label="Failed"
          value={String(stats?.failedPayments ?? 0)}
          icon={<XCircle className="w-6 h-6 text-red-600 dark:text-red-400" />}
          iconWrapClass="bg-red-100 dark:bg-red-900/30"
        />
      </div>

      {/* Search + Filters */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-text-secondary" />
          <input
            type="text"
            placeholder="Search by customer, transaction, or description..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="input input-bordered pl-10 w-full"
          />
          {searchInput && (
            <button
              onClick={() => setSearchInput('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 btn btn-ghost btn-circle btn-xs"
              aria-label="Clear search"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filters.status}
            onChange={(e) =>
              setFilters({ status: e.target.value as PaymentStatus | 'all' })
            }
            className="input input-bordered w-44"
            aria-label="Filter by status"
          >
            <option value="all">All statuses</option>
            {PAYMENT_STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {PAYMENT_STATUS_LABELS[s]}
              </option>
            ))}
          </select>
          <select
            value={filters.method}
            onChange={(e) =>
              setFilters({ method: e.target.value as PaymentMethod | 'all' })
            }
            className="input input-bordered w-44"
            aria-label="Filter by method"
          >
            <option value="all">All methods</option>
            {PAYMENT_METHOD_OPTIONS.map((m) => (
              <option key={m} value={m}>
                {PAYMENT_METHOD_LABELS[m]}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Loading State */}
      {isLoading && (
        <div className="card card-elevated p-12 text-center">
          <div className="w-16 h-16 bg-surface-overlay rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-text-tertiary" />
          </div>
          <p className="text-text-secondary">Loading payments…</p>
        </div>
      )}

      {/* Payment Table */}
      {!isLoading && (
        <PaymentTable
          payments={payments}
          onProcess={canReconcile ? handleProcess : undefined}
          onFail={canReconcile ? handleFail : undefined}
          onReconcile={canReconcile ? handleReconcile : undefined}
        />
      )}

      {/* Result count */}
      {!isLoading && total > 0 && (
        <p className="text-sm text-text-secondary">
          Showing {payments.length} of {total} payment{total !== 1 ? 's' : ''}
        </p>
      )}

      {/* Record Payment Modal */}
      {canCreate && (
        <PaymentModal
          isOpen={showCreateModal}
          onClose={() => setShowCreateModal(false)}
          onSuccess={handleCreateSuccess}
          onPending={loadStats}
        />
      )}
    </div>
  )
}
